import { verifyBoundBlockStructural } from '../_shared/bound-block-structural-gate'
import type { CogoKnowledgeRepository, Example } from '@cogo/kg-repository'
import { bindExample } from '@cogo/binder'
import { sanitizePatternToken } from '../_shared/sanitize-pattern'
import { extractBindDefaults } from './bind-defaults-extractor'
import { defaultResolvers } from './default-resolvers'
import { ontologyCandidateQualityIssue, pickTopOne } from './pick'
import type { ChannelInput, ChannelOutput, DispatchOpts, RenderedTimings } from './types'

const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now())

function aborted(signal?: AbortSignal): boolean {
  return Boolean(signal?.aborted)
}

function missAborted(): ChannelOutput {
  return { kind: 'miss', reason: 'aborted' }
}

async function resolveExample(
  input: ChannelInput,
  kg: CogoKnowledgeRepository,
): Promise<{ ex: Example | null; lookupMs: number }> {
  const t0 = now()
  if (input.kind === 'examplePick') {
    // Single-id lookup. kg-repository's FindExamplesQuery does NOT formally
    // expose an `id` predicate (requires semanticType OR pattern as the
    // SPARQL anchor), so we (1) try the `id` filter first for impls that
    // honor it client-side (HybridRepository may), then (2) fall back to a
    // wide pattern-anchored search and filter by id suffix in-memory.
    // Suffix match accommodates real Repository IRIs
    // (`https://cogo.xyz/instances/example/<id>`) vs unit-test bare ids.
    try {
      const r = await kg.findExamples({ id: input.exampleId } as never)
      if (r.data.length > 0) {
        const direct =
          r.data.find((e) => e.id === input.exampleId) ??
          r.data.find((e) => e.id.endsWith(`/${input.exampleId}`)) ??
          r.data[0]
        return { ex: direct, lookupMs: now() - t0 }
      }
    } catch {
      /* fall through to wide search */
    }
    // Fallback: wide pattern search across the corpus, filter client-side.
    // Pattern label derived from the example id prefix when possible
    // (e.g. "auth-login" → "auth-form" via shared corpus convention) — but
    // we just use the id itself as a pattern hint and let the repo expand.
    // Concretely, we walk the 9 known semanticType anchors to maximize hit.
    const anchors = [
      'https://cogo.xyz/instances/semantic-type/section',
      'https://cogo.xyz/instances/semantic-type/container',
      'https://cogo.xyz/instances/semantic-type/card',
      'https://cogo.xyz/instances/semantic-type/form',
    ]
    for (const semanticType of anchors) {
      const r = await kg.findExamples({ semanticType, k: 9999 } as never)
      const ex =
        r.data.find((e) => e.id === input.exampleId) ??
        r.data.find((e) => e.id.endsWith(`/${input.exampleId}`)) ??
        null
      if (ex) return { ex, lookupMs: now() - t0 }
    }
    return { ex: null, lookupMs: now() - t0 }
  }
  // intent mode
  const k = input.k ?? 5
  // Sanitize the pattern token before it reaches the repository — `findExamples`
  // embeds it into a SPARQL IRI position (`local-wasm-repository.ts:356-358`),
  // and oxigraph rejects whitespace + non-ASCII codepoints. Korean intents like
  // "회의 일정 관리 SaaS" previously surfaced as `kg-init-failed` and blocked
  // the router fall-through (UX e2e 2026-04-28, issues #13/#16). Mirrors
  // `mock-corpus-picker.ts:88` for symmetry across channels.
  const sanitizedPattern = input.pattern ? sanitizePatternToken(input.pattern) : ''
  const r = await kg.findExamples({
    semanticType: input.semanticType,
    pattern: sanitizedPattern || undefined,
    dsId: input.dsId,
    locale: input.locale,
    k,
  } as never)
  const picked = pickTopOne(r.data, {
    semanticType: input.semanticType,
    dsId: input.dsId,
  })
  return { ex: picked, lookupMs: now() - t0 }
}

export async function dispatchChannelOne(
  input: ChannelInput,
  kg: CogoKnowledgeRepository,
  opts: DispatchOpts = {},
): Promise<ChannelOutput> {
  const tStart = now()
  if (aborted(opts.signal)) return missAborted()

  let resolved: { ex: Example | null; lookupMs: number }
  try {
    resolved = await resolveExample(input, kg)
  } catch (err) {
    // Query-time throws (SPARQL parse, oxigraph errors, etc.) are corpus-side
    // — the lookup couldn't produce candidates, so the router should fall
    // through to Channel 2 LLM generation. WASM-load / module-init failures
    // are gated upstream by `useChannelDispatch:32` (`if (!ready || !repo)
    // return`), so by the time this runs, all kg throws are query-time.
    // Reclassifying as `no-candidates` (corpus reason) instead of
    // `kg-init-failed` (infra reason) lets `routeChannel:28` proceed.
    return {
      kind: 'miss',
      reason: 'no-candidates',
      details: err instanceof Error ? err.message : String(err),
    }
  }
  const { ex, lookupMs } = resolved
  if (aborted(opts.signal)) return missAborted()

  if (!ex) return { kind: 'miss', reason: 'no-candidates' }
  const qualityIssue = ontologyCandidateQualityIssue(ex)
  if (qualityIssue) {
    return {
      kind: 'miss',
      reason: 'no-candidates',
      details: `Ontology candidate ${ex.id} rejected: ${qualityIssue}`,
      exampleId: ex.id,
    }
  }
  const template = ex.template ?? ex.blockJson
  if (!template) {
    return { kind: 'miss', reason: 'no-template', exampleId: ex.id }
  }

  const blockDefaults = extractBindDefaults(template, ex.blockJson)
  const resolvers = defaultResolvers(ex.bindingSchema, input.resolverOverrides, blockDefaults)

  const tBind0 = now()
  let output: unknown
  let warnings: string[]
  try {
    const r = bindExample(template as never, resolvers as never)
    output = r.output
    warnings = r.warnings
  } catch (err) {
    return {
      kind: 'miss',
      reason: 'bind-throw',
      details: err instanceof Error ? err.message : String(err),
      exampleId: ex.id,
    }
  }
  const bindMs = now() - tBind0

  // Review D-7 — structural gate, AUDIT mode: Channel-1 shipped bound blocks
  // with no gate at all. Observe + warn (never reject, never swap the served
  // root) so serving stays byte-identical while gate rejections become
  // visible in bindWarnings/console. Enforce mode is a later, flagged step.
  try {
    const audit = verifyBoundBlockStructural(output)
    warnings = [
      ...warnings,
      ...audit.warnings,
      ...(audit.ok ? [] : audit.errors.map((e) => `structural-gate(audit):${e}`)),
    ]
    if (!audit.ok) {
      console.warn(
        `[channel-1] structural gate AUDIT — serving ${ex.id} despite rejection: ${audit.errors.join('; ')}`,
      )
    }
  } catch (err) {
    // The audit itself must never break serving.
    console.warn(`[channel-1] structural gate audit failed to run: ${err instanceof Error ? err.message : String(err)}`)
  }

  if (aborted(opts.signal)) return missAborted()

  // Synthesize single-block AppV2 input.
  //
  // The runtime (`@cogo/react-renderer-v2` `AppRuntime`) consumes the canonical
  // `App` shape from `@cogo/types-v2`: `pages[i]` MUST carry
  // `{ id, route, layout, blocks, blockLayout }`. Earlier prototype used
  // `{ id, blockRefs }` which crashed `scorePageSpecificity` (`p.route.split`
  // on undefined) at `app-runtime.ts:1806`. `rootBlockId` is retained as an
  // additive convenience field — unit tests assert it, runtime ignores it.
  // `layout: 'stack'` is canonical (one of two layouts `syncNavigation` skips
  // silently at `app-runtime.ts:1200`); `'single'` produced spurious console
  // noise on every pick (UX eval F-5, 2026-04-28).
  const manifest = {
    rootBlockId: ex.id,
    pages: [
      {
        id: 'home',
        route: '/',
        layout: 'stack',
        blocks: [ex.id],
        blockLayout: {},
      },
    ],
  } as never
  // `bindExample().output` is a bare `UiElement` (the bound template root).
  // `AppRuntime.buildPageTree()` reads `block.ui.root` (`enrichBlock(name)?.ui?.root`
  // at `app-runtime.ts:519`); without the `{root: ...}` wrapper the runtime falls
  // through `if (!root) continue` and skips the block, leaving an empty layout
  // (UX eval F-1, 2026-04-28). E2E baselines passed because they only checked
  // the `canvas-rendered` testid wrapper, not the actual rendered subtree —
  // see `e2e/labs/channel-1.spec.ts` "block content visibility" baseline below.
  const blocks = {
    [ex.id]: {
      meta: { id: ex.id, name: ex.id } as never,
      ui: { root: output } as never,
      events: {} as never,
      flow: {} as never,
      state: {} as never,
      data: {} as never,
    },
  }

  const totalMs = now() - tStart
  const timings: RenderedTimings = { lookupMs, bindMs, totalMs }

  return {
    kind: 'rendered',
    channel: 1,
    exampleId: ex.id,
    manifest,
    blocks,
    bindWarnings: warnings,
    timings,
    shapeSha: opts.shapeSha ?? 'unknown',
  }
}
